import { useEffect, type RefObject } from 'react';
import { clsx } from 'clsx';
import {
  TrackingPropsInterface,
  constants,
  getClickTrackValue,
  isDcaTrackingPayloadReady,
  trackImpression,
} from '@marriott/mi-ui-library';

export interface UseTrackingParameters {
  ref: RefObject<HTMLElement>;
  trackingProperties?: TrackingPropsInterface;
  isExternal?: boolean;
  custom_click_track_value?: string;
  preTrackVal?: string;
  text?: string;
}

export function useTracking({
  ref,
  trackingProperties = {},
  isExternal = false,
  custom_click_track_value = '',
  preTrackVal = '',
  text = '',
}: UseTrackingParameters) {
  useEffect(() => {
    const {
      clickTrack = false,
      impressionTrack = false,
      impressionCount = false,
      isDCAEnabled = false,
      trackingContentPosition = '',
      trackingDescription = '',
      trackingOfferType = '',
      trackingTag = '',
      atCCeVar48 = '',
      compName = '',
      additionalTrackingVariables = '',
      merchandisingCategory = '',
      isCoBrand: explicitIsCoBrand,
      type: trackingType,
    } = trackingProperties;

    const shouldEnableTracking = clickTrack || impressionTrack || custom_click_track_value.trim().length;
    const isTrackingComplete = ref?.current?.classList?.contains('track-completed');

    if (!shouldEnableTracking || isTrackingComplete || !ref?.current) {
      return;
    }

    // WEB-191033: DCA-driven banners (MButton + useTracking) render BEFORE the
    // Adobe Target mbox resolves. Defer stamping for DCA elements until the
    // payload is meaningful. The shared `isDcaTrackingPayloadReady` helper is
    // the single source of truth shared with Button/Link so every leaf behaves
    // identically; it also intentionally ignores `custom_click_track_value` as
    // evidence of meaningfulness for DCA elements, because legacy wrappers
    // often pre-built CTVs out of placeholder tokens.
    if (!isDcaTrackingPayloadReady(trackingProperties, custom_click_track_value)) {
      return;
    }

    // WEB-191033: Prefer an explicit boolean `isCoBrand` from trackingProperties (e.g.
    // DCA-driven cobrand banners/cards). Fall back to deriving from `type` or
    // `merchandisingCategory` for legacy consumers that don't set `isCoBrand` directly.
    const resolvedIsCoBrand =
      typeof explicitIsCoBrand === 'boolean'
        ? explicitIsCoBrand
        : trackingType
        ? trackingType === 'CoBrand'
        : merchandisingCategory === constants.COBRAND_NON_POINTS_MATH;

    Object.assign(ref.current.dataset, {
      isCoBrand: resolvedIsCoBrand,
      isImpressionTrack: impressionTrack,
      isClickTrack: clickTrack,
      trackingContentPosition,
      trackingDescription,
      trackingOfferType,
      trackingTag,
      atCCeVar48,
      compName,
      impressionCount,
      additionalTrackingVariables,
      isDCAEnabled,
      custom_click_track_value,
    });

    ref.current?.classList?.add(
      ...clsx(clickTrack && 'custom_click_track', !impressionTrack && 'no-impression').split(' ')
    );
    if (!custom_click_track_value) {
      getClickTrackValue(isExternal ? 'external' : 'internal', trackingProperties, ref);
    }
    if (impressionTrack) {
      trackImpression(trackingProperties, text, preTrackVal);
    }
    ref.current?.classList?.add('track-completed');
  }, [ref, trackingProperties, isExternal, custom_click_track_value, preTrackVal, text]);
}
